import React, { useState, useEffect } from 'react';
import html2canvas from 'html2canvas';
import { FaPrint, FaFilePdf, FaDownload, FaTimes, FaSpinner, FaInfoCircle } from 'react-icons/fa';

/**
 * DocumentPreview
 * Professional Document Viewer & Multi-Page PDF / Print System
 * - Interactive A4 vector preview on screen
 * - Native multi-page vector A4 print / "Enregistrer en PDF" (zero cut rows, repeated table headers)
 * - High-res image download (JPG / PNG)
 */
const DocumentPreview = ({ targetId, onClose, fileName, onAfterGenerate }) => {
    const [htmlContent, setHtmlContent] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');

    useEffect(() => {
        // Load direct HTML for instant crisp preview
        const element = document.getElementById(targetId);
        if (element) {
            setHtmlContent(element.innerHTML);
        }
    }, [targetId]);

    const getCaptureElement = () => {
        return document.getElementById('preview-a4-sheet-container') || document.getElementById(targetId);
    };

    /**
     * Native Multi-page A4 Print / "Enregistrer en PDF"
     * Uses an isolated iframe with precise print stylesheets,
     * repeating table headers and avoiding row splits with sub-pixel precision.
     */
    const handleNativePrint = () => {
        const element = document.getElementById(targetId);
        if (!element) return;

        // Remove any previous print iframe
        const oldIframe = document.getElementById('doc-print-iframe');
        if (oldIframe) oldIframe.remove();

        const iframe = document.createElement('iframe');
        iframe.id = 'doc-print-iframe';
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        iframe.style.visibility = 'hidden';
        document.body.appendChild(iframe);

        const doc = iframe.contentWindow.document;
        doc.open();
        doc.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8">
                <title>${fileName || 'Document'} - ENOVADENT</title>
                <style>
                    @page {
                        size: A4 portrait;
                        margin: 12mm 15mm 15mm 15mm;
                    }
                    * {
                        box-sizing: border-box;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    html, body {
                        margin: 0;
                        padding: 0;
                        background: white;
                        font-family: 'Segoe UI', Arial, Helvetica, sans-serif;
                        color: #0f172a;
                        width: 100%;
                    }
                    table {
                        width: 100% !important;
                        border-collapse: collapse !important;
                        page-break-inside: auto;
                    }
                    thead {
                        display: table-header-group !important;
                    }
                    tfoot {
                        display: table-footer-group !important;
                    }
                    tr {
                        page-break-inside: avoid !important;
                        break-inside: avoid !important;
                    }
                    .no-break {
                        page-break-inside: avoid !important;
                        break-inside: avoid !important;
                    }
                </style>
            </head>
            <body>
                ${element.innerHTML}
            </body>
            </html>
        `);
        doc.close();

        setTimeout(() => {
            try {
                iframe.contentWindow.focus();
                iframe.contentWindow.print();
                if (onAfterGenerate) onAfterGenerate();
            } catch (err) {
                console.error("Print error:", err);
                window.print();
            }
            setTimeout(() => {
                if (iframe && iframe.parentNode) iframe.remove();
            }, 2000);
        }, 350);
    };

    /**
     * Download Image (JPG / PNG)
     * Uses the visible rendered sheet.
     */
    const downloadImage = async (format = 'jpg') => {
        setIsGenerating(true);
        setStatusMessage(`Génération de l'image ${format.toUpperCase()}...`);
        const element = getCaptureElement();
        if (!element) {
            setIsGenerating(false);
            return;
        }

        try {
            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                logging: false
            });

            const mime = format === 'png' ? 'image/png' : 'image/jpeg';
            const imgData = canvas.toDataURL(mime, 0.95);

            const link = document.createElement('a');
            link.href = imgData;
            link.download = `${fileName}.${format}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            if (onAfterGenerate) onAfterGenerate();
        } catch (err) {
            console.error("Error generating image:", err);
        } finally {
            setIsGenerating(false);
            setStatusMessage('');
        }
    };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.85)', zIndex: 9999,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(6px)', padding: '10px',
            width: '100vw', maxWidth: '100vw', minWidth: '100vw', flex: 'none', margin: 0
        }}>
            {/* Modal Container */}
            <div style={{
                backgroundColor: '#f8fafc',
                borderRadius: '16px',
                width: '100%',
                maxWidth: '920px',
                height: '94vh',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                overflow: 'hidden',
                position: 'relative',
                border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
                {/* Header Toolbar */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '14px 20px',
                    backgroundColor: 'white',
                    borderBottom: '1px solid #e2e8f0',
                    zIndex: 10,
                    flexWrap: 'wrap',
                    gap: '10px'
                }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a', fontFamily: 'system-ui' }}>
                            Aperçu du document
                        </span>
                        <span style={{ fontSize: '12px', color: '#64748b', fontFamily: 'system-ui' }}>
                            {fileName}
                        </span>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                        {/* Primary Print / Save as PDF Button */}
                        <button
                            onClick={handleNativePrint}
                            disabled={isGenerating}
                            className="btn btn-primary px-3 py-2 fw-bold shadow-sm"
                            style={{
                                borderRadius: '10px',
                                fontSize: '13.5px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '7px',
                                backgroundColor: '#1e3a8a',
                                borderColor: '#1e3a8a'
                            }}
                            title="Ouvre la boîte d'impression pour enregistrer en PDF multi-pages ou imprimer directement"
                        >
                            <FaFilePdf size={15} /> Enregistrer en PDF / Imprimer
                        </button>

                        {/* JPG Image Download */}
                        <button
                            onClick={() => downloadImage('jpg')}
                            disabled={isGenerating}
                            className="btn btn-outline-secondary px-3 py-2 fw-semibold"
                            style={{
                                borderRadius: '10px',
                                fontSize: '12.5px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px'
                            }}
                        >
                            <FaDownload size={11} /> Image JPG
                        </button>

                        {/* PNG Image Download */}
                        <button
                            onClick={() => downloadImage('png')}
                            disabled={isGenerating}
                            className="btn btn-outline-secondary px-3 py-2 fw-semibold"
                            style={{
                                borderRadius: '10px',
                                fontSize: '12.5px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px'
                            }}
                        >
                            <FaDownload size={11} /> PNG
                        </button>

                        {/* Close Modal */}
                        <button
                            onClick={onClose}
                            className="btn btn-outline-danger px-3 py-2 fw-bold ms-1"
                            style={{
                                borderRadius: '10px',
                                fontSize: '13px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px'
                            }}
                        >
                            <FaTimes size={12} /> Fermer
                        </button>
                    </div>
                </div>

                {/* Helpful Tip Banner */}
                <div style={{
                    backgroundColor: '#eff6ff',
                    color: '#1e3a8a',
                    padding: '8px 20px',
                    fontSize: '12.5px',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    borderBottom: '1px solid #bfdbfe'
                }}>
                    <FaInfoCircle size={14} className="text-primary flex-shrink-0" />
                    <span>
                        <strong>Pour enregistrer en PDF :</strong> Cliquez sur le bouton bleu <strong>« Enregistrer en PDF / Imprimer »</strong>, puis choisissez <em>« Enregistrer au format PDF »</em> comme destination. Vos 4 pages seront parfaitement découpées sans aucune coupure de ligne.
                    </span>
                </div>

                {/* Loading indicator bar */}
                {isGenerating && (
                    <div style={{
                        backgroundColor: '#fef3c7',
                        color: '#92400e',
                        padding: '8px 20px',
                        fontSize: '13px',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        borderBottom: '1px solid #fde68a'
                    }}>
                        <FaSpinner className="fa-spin" size={14} />
                        {statusMessage || 'Traitement en cours...'}
                    </div>
                )}

                {/* Content Area - Realistic A4 Document Sheet */}
                <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '25px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    backgroundColor: '#e2e8f0'
                }}>
                    <div
                        id="preview-a4-sheet-container"
                        style={{
                            width: '210mm',
                            maxWidth: '100%',
                            backgroundColor: '#ffffff',
                            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                            borderRadius: '4px',
                            padding: '15mm 18mm',
                            boxSizing: 'border-box',
                            margin: '0 auto',
                            color: '#0f172a'
                        }}
                        dangerouslySetInnerHTML={{ __html: htmlContent }}
                    />
                </div>
            </div>
        </div>
    );
};

export default DocumentPreview;
